import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Check, X, RotateCcw, Loader2, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/Skeleton";
import { useDocumentTitle } from "@/lib/useDocumentTitle";
import { cn } from "@/lib/utils";

export default function LearnQuizPage() {
    const { slug } = useParams();
    const navigate = useNavigate();

    const [chapter, setChapter] = useState(null);
    const [chapters, setChapters] = useState([]);
    const [answers, setAnswers] = useState({}); // {question_id: option_id}
    const [submitting, setSubmitting] = useState(false);
    const [result, setResult] = useState(null);
    useDocumentTitle(chapter ? `${chapter.title} quiz` : "Quiz");

    useEffect(() => {
        let cancelled = false;
        Promise.all([
            api.get(`/learn/chapters/${slug}`),
            api.get("/learn/chapters"),
        ])
            .then(([c, all]) => {
                if (cancelled) return;
                setChapter(c.data);
                setChapters(all.data);
            })
            .catch((e) => {
                if (cancelled) return;
                if (e?.response?.status === 404) {
                    toast.error("Chapter not found");
                    navigate("/learn", { replace: true });
                } else {
                    toast.error("Could not load quiz");
                }
            });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [slug]);

    const nextChapterSlug = useMemo(() => {
        if (!chapter || !chapters.length) return null;
        const sameModule = chapters
            .filter((c) => c.module_slug === chapter.module_slug)
            .sort((a, b) => a.order - b.order);
        const idx = sameModule.findIndex((c) => c.slug === chapter.slug);
        return idx >= 0 && idx < sameModule.length - 1 ? sameModule[idx + 1].slug : null;
    }, [chapter, chapters]);

    const allAnswered =
        chapter && chapter.quiz.length > 0 &&
        chapter.quiz.every((q) => answers[q.id]);

    const handleSubmit = async () => {
        if (!chapter || !allAnswered) return;
        setSubmitting(true);
        try {
            const payload = {
                answers: chapter.quiz.map((q) => ({
                    question_id: q.id,
                    option_id: answers[q.id],
                })),
            };
            const res = await api.post(`/learn/chapters/${slug}/quiz`, payload);
            setResult(res.data);
            window.scrollTo({ top: 0, behavior: "smooth" });
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Quiz submission failed");
        } finally {
            setSubmitting(false);
        }
    };

    const handleRetake = () => {
        setResult(null);
        setAnswers({});
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    if (!chapter) {
        return (
            <Layout>
                <div className="mx-auto max-w-2xl space-y-4 py-12">
                    <Skeleton className="h-3 w-24" />
                    <Skeleton className="h-10 w-3/4" />
                    {Array.from({ length: 4 }).map((_, i) => (
                        <Skeleton key={i} className="h-32 w-full rounded-2xl" />
                    ))}
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="mx-auto max-w-2xl space-y-6 pb-12 animate-fade-in-up" data-testid="learn-quiz-page">
                <div>
                    <Button asChild variant="ghost" size="sm" className="-ml-2 text-muted-foreground" data-testid="learn-quiz-back-to-chapter">
                        <Link to={`/learn/chapter/${chapter.slug}`}>
                            <ArrowLeft className="h-4 w-4" />
                            Back to chapter
                        </Link>
                    </Button>
                </div>

                <header className="space-y-2">
                    <p className="text-xs font-semibold uppercase tracking-wider text-primary">
                        Chapter {chapter.order} — Quiz
                    </p>
                    <h1 className="text-3xl font-semibold tracking-tight text-foreground" data-testid="learn-quiz-title">
                        {chapter.title}
                    </h1>
                    <p className="text-sm text-muted-foreground">
                        5 multiple-choice questions. Pass at 60% or higher.
                    </p>
                </header>

                {result ? (
                    <ResultsView
                        result={result}
                        chapter={chapter}
                        nextChapterSlug={nextChapterSlug}
                        onRetake={handleRetake}
                    />
                ) : (
                    <QuestionsForm
                        quiz={chapter.quiz}
                        answers={answers}
                        onChange={(qid, oid) => setAnswers((a) => ({ ...a, [qid]: oid }))}
                        onSubmit={handleSubmit}
                        submitting={submitting}
                        allAnswered={allAnswered}
                    />
                )}
            </div>
        </Layout>
    );
}

const QuestionsForm = ({ quiz, answers, onChange, onSubmit, submitting, allAnswered }) => {
    const answeredCount = Object.values(answers).filter(Boolean).length;
    return (
        <form
            onSubmit={(e) => { e.preventDefault(); onSubmit(); }}
            className="space-y-6 pb-24"
            data-testid="learn-quiz-form"
        >
            {quiz.map((q, idx) => (
                <fieldset
                    key={q.id}
                    data-testid={`learn-quiz-question-${q.id}`}
                    className="space-y-3 rounded-2xl border border-border/80 bg-card p-5 shadow-sm"
                >
                    <legend className="px-1">
                        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                            Question {idx + 1} of {quiz.length}
                        </p>
                        <p className="mt-1 text-base font-medium text-foreground">{q.prompt}</p>
                    </legend>
                    <div className="space-y-2">
                        {q.options.map((o) => {
                            const checked = answers[q.id] === o.id;
                            return (
                                <label
                                    key={o.id}
                                    data-testid={`learn-quiz-option-${q.id}-${o.id}`}
                                    className={cn(
                                        "flex cursor-pointer items-start gap-3 rounded-xl border px-4 py-3 text-sm transition-all",
                                        checked
                                            ? "border-primary bg-primary/5 ring-1 ring-primary"
                                            : "border-border bg-background hover:border-primary/40 hover:bg-secondary"
                                    )}
                                >
                                    <input
                                        type="radio"
                                        name={q.id}
                                        value={o.id}
                                        checked={checked}
                                        onChange={() => onChange(q.id, o.id)}
                                        className="mt-1 h-4 w-4 accent-primary"
                                    />
                                    <span className="flex-1 leading-relaxed text-foreground">
                                        <span className="mr-2 inline-flex h-5 w-5 items-center justify-center rounded-full border border-border bg-secondary text-[10px] font-semibold uppercase">
                                            {o.id}
                                        </span>
                                        {o.text}
                                    </span>
                                </label>
                            );
                        })}
                    </div>
                </fieldset>
            ))}

            <div className="sticky bottom-3 z-10 flex items-center justify-between gap-3 rounded-2xl border border-border/80 bg-background/95 px-4 py-3 shadow-lg backdrop-blur">
                <p className="text-xs text-muted-foreground tabular-nums" data-testid="learn-quiz-progress">
                    {answeredCount} of {quiz.length} answered
                </p>
                <Button
                    type="submit"
                    disabled={!allAnswered || submitting}
                    className="bg-primary hover:bg-primary-hover"
                    data-testid="learn-quiz-submit-button"
                >
                    {submitting ? (
                        <>
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Scoring…
                        </>
                    ) : (
                        <>
                            Submit quiz
                            <ArrowRight className="h-4 w-4" />
                        </>
                    )}
                </Button>
            </div>
        </form>
    );
};

const ResultsView = ({ result, chapter, nextChapterSlug, onRetake }) => {
    const passed = result.passed;
    const tone = passed
        ? "border-emerald-200 bg-emerald-50 text-emerald-900"
        : "border-amber-200 bg-amber-50 text-amber-900";
    const headline = passed
        ? result.score === result.total
            ? "Perfect — every answer dialled in."
            : "Solid work — you've got the shape of it."
        : "Almost there — review and try again.";

    return (
        <div className="space-y-6" data-testid="learn-quiz-results">
            <Card className={cn("shadow-sm", tone)}>
                <CardContent className="space-y-3 p-6">
                    <div className="flex items-baseline justify-between gap-4">
                        <div>
                            <p className="text-xs font-semibold uppercase tracking-wider opacity-80">
                                {passed ? "Chapter complete" : "Quiz result"}
                            </p>
                            <p className="mt-1 text-3xl font-semibold tracking-tight">
                                <span data-testid="learn-quiz-result-score">{result.score}</span>
                                {" / "}
                                {result.total}
                            </p>
                        </div>
                        <span
                            className={cn(
                                "rounded-full border bg-white/60 px-3 py-1 text-sm font-semibold tabular-nums",
                                passed ? "border-emerald-300" : "border-amber-300"
                            )}
                            data-testid="learn-quiz-result-percentage"
                        >
                            {result.percentage}%
                        </span>
                    </div>
                    <p className="text-base font-medium">{headline}</p>
                    <p className="text-xs opacity-80" data-testid="learn-quiz-result-meta">
                        Attempt #{result.attempt_number}
                        <span className="mx-1.5 opacity-60">·</span>
                        Best score so far: {result.best_score} / {result.total}
                    </p>
                </CardContent>
            </Card>

            <ul className="space-y-3" data-testid="learn-quiz-feedback-list">
                {result.per_question.map((pq, idx) => {
                    const tone = pq.is_correct
                        ? "border-emerald-200 bg-emerald-50/60"
                        : "border-rose-200 bg-rose-50/60";
                    const Icon = pq.is_correct ? Check : X;
                    const iconTone = pq.is_correct ? "bg-emerald-500" : "bg-rose-500";
                    return (
                        <li
                            key={pq.question_id}
                            data-testid={`learn-quiz-feedback-${pq.question_id}`}
                            className={cn("rounded-2xl border p-4 shadow-sm", tone)}
                        >
                            <div className="flex items-start gap-3">
                                <span className={cn("mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-white", iconTone)}>
                                    <Icon className="h-3.5 w-3.5" />
                                </span>
                                <div className="min-w-0 space-y-2">
                                    <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                                        Question {idx + 1}
                                    </p>
                                    <p className="text-sm font-medium text-foreground">{pq.prompt}</p>
                                    <p className="text-xs text-muted-foreground" data-testid={`learn-quiz-feedback-your-${pq.question_id}`}>
                                        Your answer:{" "}
                                        <span className={cn("font-semibold uppercase", pq.is_correct ? "text-emerald-700" : "text-rose-700")}>
                                            {pq.your_option_id}
                                        </span>
                                        {!pq.is_correct && (
                                            <>
                                                <span className="mx-1.5">·</span>
                                                Correct:{" "}
                                                <span
                                                    className="font-semibold uppercase text-emerald-700"
                                                    data-testid={`learn-quiz-feedback-correct-${pq.question_id}`}
                                                >
                                                    {pq.correct_option_id}
                                                </span>
                                            </>
                                        )}
                                    </p>
                                    <p className="text-sm leading-relaxed text-foreground">{pq.explanation}</p>
                                </div>
                            </div>
                        </li>
                    );
                })}
            </ul>

            <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
                <Button
                    onClick={onRetake}
                    variant="outline"
                    data-testid="learn-quiz-retake-button"
                >
                    <RotateCcw className="h-4 w-4" />
                    Retake quiz
                </Button>
                <div className="flex flex-wrap items-center gap-2">
                    <Button asChild variant="ghost" data-testid="learn-quiz-back-button">
                        <Link to={`/learn/chapter/${chapter.slug}`}>Back to chapter</Link>
                    </Button>
                    {nextChapterSlug ? (
                        <Button asChild className="bg-primary hover:bg-primary-hover" data-testid="learn-quiz-next-chapter-button">
                            <Link to={`/learn/chapter/${nextChapterSlug}`}>
                                Next chapter
                                <ArrowRight className="h-4 w-4" />
                            </Link>
                        </Button>
                    ) : (
                        <Button asChild className="bg-primary hover:bg-primary-hover" data-testid="learn-quiz-finish-button">
                            <Link to={`/learn/${chapter.module_slug}`}>
                                <Sparkles className="h-4 w-4" />
                                Back to module
                            </Link>
                        </Button>
                    )}
                </div>
            </div>
        </div>
    );
};
